/* ============================================================
   Static data for the gallery: image helpers, seed paintings,
   seed settings and the money formatter.

   The seed paintings below are only used in LOCAL PREVIEW mode
   (when Supabase isn't configured). The live site reads paintings
   from the database instead — see src/lib/api.js.
   ============================================================ */

const ASSET = (name) => `${process.env.PUBLIC_URL || ""}/assets/${name}`;

export const LOGO = {
  light: ASSET("logo-light.png"), // umber ink for light mode
  dark: ASSET("logo-dark.png"), // ivory ink for dark mode
};

/* Built-in painting photos live in public/assets/paintings.
   Each has a full-size file and a "-sm" thumbnail for grids.
   A painting's `images` entry like "builtin:amber-tide" points at one of them. */
const BUILTIN = {
  "golden-horizon": "webp",
  "still-waters": "jpg",
  "evening-sail": "jpg",
  "open-water": "jpg",
  "rose-hour": "jpg",
  "storm-light": "jpg",
  "silver-shore": "jpg",
  "amber-tide": "jpg",
};

const builtinUrl = (slug, small) =>
  ASSET(`paintings/${slug}${small ? "-sm" : ""}.${BUILTIN[slug]}`);

/* Full-size image URL for a stored image reference. */
export const resolveImg = (ref) => {
  if (ref && ref.startsWith("builtin:")) {
    const slug = ref.slice(8);
    return BUILTIN[slug] ? builtinUrl(slug, false) : ref;
  }
  return ref;
};

/* Smaller image for grids and thumbnails (falls back to the full image). */
export const thumbImg = (ref) => {
  if (ref && ref.startsWith("builtin:")) {
    const slug = ref.slice(8);
    return BUILTIN[slug] ? builtinUrl(slug, true) : ref;
  }
  return ref;
};

/* PLACEHOLDERS: titles, sizes, prices and descriptions below are starting
   points written from the photos. Edit them any time in the Studio. */
export const SEED_PAINTINGS = [
  { id: "amber-tide", title: "Amber Tide", images: ["builtin:amber-tide"], price: 520, year: "2026",
    medium: "Oil on canvas", dims: "16 × 20 in", category: "Seascapes", status: "available", featured: true,
    description: "Evening light turns the surf to amber as waves fold onto the sand and the sun settles on the horizon." },
  { id: "evening-sail", title: "Evening Sail", images: ["builtin:evening-sail"], price: 480, year: "2026",
    medium: "Oil on canvas", dims: "12 × 16 in", category: "Boats at Sea", status: "available", featured: true,
    description: "A lone sailboat rests on a mirror-calm sea while the sky melts from soft blue into honeyed gold." },
  { id: "golden-horizon", title: "Golden Horizon", images: ["builtin:golden-horizon"], price: 280, year: "2026",
    medium: "Oil on oval canvas", dims: "12 × 16 in", category: "Seascapes", status: "available", featured: true,
    description: "The last sliver of sun slips below a quiet horizon, its amber light pooling across slow, silken water. Painted on an oval canvas." },
  { id: "storm-light", title: "Storm Light", images: ["builtin:storm-light"], price: 560, year: "2026",
    medium: "Oil on canvas", dims: "16 × 20 in", category: "Seascapes", status: "available", featured: true,
    description: "A wave breaks in cold spray while the setting sun burns through a bruised, swirling sky." },
  { id: "still-waters", title: "Still Waters", images: ["builtin:still-waters"], price: 520, year: "2026",
    medium: "Oil on canvas", dims: "16 × 20 in", category: "Boats at Sea", status: "available", featured: true,
    description: "A small blue rowboat drifts in glass-clear turquoise shallows beneath towering summer clouds." },
  { id: "rose-hour", title: "Rose Hour", images: ["builtin:rose-hour"], price: 640, year: "2026",
    medium: "Oil on canvas", dims: "20 × 16 in", category: "Boats at Sea", status: "available", featured: true,
    description: "A sailboat crosses a wide, quiet bay at dusk while pink and gold light spills from the clouds into the water." },
  { id: "silver-shore", title: "Silver Shore", images: ["builtin:silver-shore"], price: 600, year: "2026",
    medium: "Oil on canvas", dims: "20 × 16 in", category: "Seascapes", status: "available", featured: true,
    description: "Low waves roll onto a dusky beach under a pearl-grey sky, the last warm light caught in the foam." },
  { id: "open-water", title: "Open Water", images: ["builtin:open-water"], price: 340, year: "2026",
    medium: "Oil on canvas", dims: "10 × 14 in", category: "Boats at Sea", status: "available", featured: false,
    description: "A white cabin boat faces the viewer across deep teal water, small clouds drifting in a bright sky." },
];

/* The three tabs on the Prints page, in the order they appear.
   `kind` is what the database stores; the labels are what visitors read. */
export const SHOP_KINDS = [
  { kind: "print", label: "Prints", one: "print", sizes: true,
    blurb: "Fine-art reproductions of the originals, printed on heavy archival paper." },
  { kind: "sticker", label: "Stickers", one: "sticker", sizes: false,
    blurb: "Weatherproof vinyl stickers taken from details in the paintings." },
  { kind: "bookmark", label: "Bookmarks", one: "bookmark", sizes: false,
    blurb: "Printed bookmarks on thick card, sealed to survive a pocket." },
];

/* Only prints come in several sizes. A sticker is one thing at one price, so it
   gets a single price field in the Studio and no size picker in the shop.
   Underneath, every item still keeps a `variants` list — a one-price item simply
   has one entry — so the cart and the checkout have a single shape to handle. */
export const hasSizes = (kind) => Boolean((SHOP_KINDS.find((k) => k.kind === kind) || {}).sizes);

/* The entry a single-price item carries. */
export const ONE_PRICE_ID = "standard";
export const onePriceVariant = (price) => ({ id: ONE_PRICE_ID, label: "Standard", price: Number(price) || 0 });

export const kindLabel = (kind) => (SHOP_KINDS.find((k) => k.kind === kind) || {}).label || "Prints";

/* Cheapest size, used for the "from $35" line on a card. */
export const fromPrice = (item) => {
  const prices = (item.variants || []).map((v) => Number(v.price)).filter((n) => Number.isFinite(n) && n > 0);
  return prices.length ? Math.min(...prices) : 0;
};

/* LOCAL PREVIEW ONLY — the live site reads shop_items from the database. */
export const SEED_SHOP = [
  { id: "print-golden-horizon", kind: "print", title: "Golden Horizon — print", images: ["builtin:golden-horizon"],
    active: true, description: "A fine-art reproduction of the original oil, printed on heavy archival paper with a small white border for framing.",
    variants: [{ id: "a4", label: "8 × 12 in", price: 35 }, { id: "a3", label: "12 × 16 in", price: 55 }] },
  { id: "print-still-waters", kind: "print", title: "Still Waters — print", images: ["builtin:still-waters"],
    active: true, description: "Printed on heavy archival paper, signed on the back.",
    variants: [{ id: "a4", label: "8 × 12 in", price: 35 }, { id: "a3", label: "12 × 16 in", price: 55 }] },
  { id: "sticker-evening-sail", kind: "sticker", title: "Evening Sail — sticker", images: ["builtin:evening-sail"],
    active: true, description: "Weatherproof vinyl. Good on a laptop, a water bottle or a sketchbook.",
    variants: [{ id: "standard", label: "Standard", price: 4 }] },
  { id: "bookmark-rose-hour", kind: "bookmark", title: "Rose Hour — bookmark", images: ["builtin:rose-hour"],
    active: true, description: "Printed on thick card and sealed, so it survives being carried around.",
    variants: [{ id: "standard", label: "Standard", price: 5 }] },
];

export const SEED_SETTINGS = {
  instagram: "https://www.instagram.com/karima_elghobri",
  youtube: "https://www.youtube.com/@KarimaElghobri",
  email: "karimaelghobri.info@gmail.com",
  statement: "I paint in oils because they let a moment breathe — layer over layer, the way memory works. Every canvas leaves my studio with a story, and I share the whole process, brushstroke by brushstroke, with the people who follow along.",
};

export const cad = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
